import { RebuySDK } from './rebuy-core-sdk.js';

if (!customElements.get('rebuy-recommendations')) {
  customElements.define(
    'rebuy-recommendations',
    class RebuyRecommendations extends HTMLElement {
      constructor() {
        super();
        this.sdk = null;
        this.recommendationType = this.getAttribute('data-recommendation-type') || 'related_products';
        this.limit = Number(this.getAttribute('data-limit')) || 8;
        this.productId = this.getAttribute('data-product-id') || null;
        this.staticId = this.getAttribute('data-static-id') || null;
        this.emptyMessage = this.getAttribute('data-empty-message') || '';
        this.cardSectionId = this.getAttribute('data-card-section-id') || 'rebuy-product-card';
        this.enableSlider = this.getAttribute('data-enable-slider') === 'true';
        this.swipeOnMobile = this.getAttribute('data-swipe-on-mobile') === 'true';
        this.columnsMobile = Number(this.getAttribute('data-columns-mobile')) || 1;
        this.columnsDesktop = Number(this.getAttribute('data-columns-desktop')) || 4;
        this.columnsTablet = Number(this.getAttribute('data-columns-tablet')) || 3;
      }

      connectedCallback() {
        this.statusEl = this.querySelector('[data-rebuy-status]');
        this.grid = this.querySelector('[data-rebuy-grid]');
        this.slider = this.querySelector('product-slider');
        this.initialize();
      }

      disconnectedCallback() {
        this.cleanup();
      }

      async initialize() {
        const apiKey = window.FoxTheme?.rebuy?.key;
        if (!apiKey) {
          this.renderEmpty();
          return;
        }

        this.sdk = new RebuySDK({ apiKey });

        if (this.sdk.session.initPromise) {
          await this.sdk.session.initPromise;
        }

        if (this.productId) {
          await this.sdk.tracking.productView(this.productId);
        }

        try {
          const products = await this.getProducts();
          if (products?.length) {
            await this.renderProducts(products);
          } else {
            this.renderEmpty();
          }
        } catch (error) {
          console.error('[RebuyRecommendations] Failed to load products:', error);
          this.renderEmpty();
        }
      }

      cleanup() {
        this.sdk = null;
      }

      getProducts() {
        const limit = this.limit;

        switch (this.recommendationType) {
          case 'related_products':
            return this.sdk.products.getRecommended({
              limit,
              shopify_product_ids: this.productId || undefined,
            });
          case 'static_recommendations':
            if (!this.staticId) return Promise.resolve([]);
            return this.sdk.products.fetchFromDataSource(this.staticId, {
              limit,
              shopify_product_ids: this.productId ? [this.productId] : [],
            });
          case 'top_sellers':
            return this.sdk.products.getTopSellers({ limit });
          case 'similar_products':
            if (!this.productId) return Promise.resolve([]);
            return this.sdk.products.getSimilar({
              limit,
              shopify_product_ids: [this.productId],
            });
          case 'recently_viewed':
            return this.sdk.products.getRecentlyViewed({ limit });
          case 'previously_purchased':
            return this.sdk.products.getPurchased({ limit });
          default:
            return this.sdk.products.getRecommended({
              limit,
              shopify_product_ids: this.productId || undefined,
            });
        }
      }

      async renderProducts(products) {
        const cardPromises = products.map(async (product) => {
          if (!product.handle) return null;

          try {
            const response = await fetch(`/products/${product.handle}?section_id=${this.cardSectionId}`);
            if (!response.ok) return null;
            const html = await response.text();
            return this.extractCardHtml(html);
          } catch {
            return null;
          }
        });

        const cards = (await Promise.all(cardPromises)).filter(Boolean);

        if (!cards.length) {
          this.renderEmpty();
          return;
        }

        if (this.grid) {
          this.grid.innerHTML = cards.join('');
        }

        this.hideStatus();
        await customElements.whenDefined('product-slider');
        this.updateSlider(cards.length);
        this.classList.add('is-loaded');
        this.removeAttribute('aria-busy');
      }

      extractCardHtml(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sectionEl = doc.querySelector('.shopify-section');
        if (!sectionEl) return null;
        if (!sectionEl.querySelector('.product-card')) return null;
        return `<div class="f-column swiper-slide">${sectionEl.innerHTML}</div>`;
      }

      hideStatus() {
        if (!this.statusEl) return;
        this.statusEl.hidden = true;
      }

      updateSlider(totalItems) {
        if (!this.slider) return;

        this.slider.hidden = false;
        this.slider.totalItems = totalItems;
        this.slider.dataset.totalItems = String(totalItems);

        const wrap = this.slider.querySelector('.products-grid-wrap');
        if (wrap && this.swipeOnMobile && totalItems > this.columnsMobile) {
          wrap.classList.add('swipe-mobile', `swipe-mobile--${this.columnsMobile}-cols`);
          if (this.grid) this.grid.classList.add('swipe-mobile__inner');
        }

        const controls = this.slider.querySelector('.swiper-controls');
        if (controls) {
          if (totalItems > this.columnsTablet) {
            controls.classList.add('md:flex');
          } else {
            controls.classList.remove('md:flex');
          }

          if (totalItems > this.columnsDesktop) {
            controls.classList.add('lg:flex');
            controls.classList.remove('lg:hidden');
          } else {
            controls.classList.remove('lg:flex');
            controls.classList.add('lg:hidden');
          }
        }

        if (typeof this.slider.destroySlider === 'function' && typeof this.slider.sliderInstance === 'object') {
          this.slider.destroySlider();
        }

        this.slider.enableSlider = this.enableSlider;
        this.slider.dataset.enableSlider = String(this.enableSlider);

        if (this.enableSlider && typeof this.slider.init === 'function') {
          this.slider.init();
          if (typeof this.slider.calcNavButtonsPosition === 'function') {
            this.slider.calcNavButtonsPosition();
            this.grid?.querySelectorAll('img').forEach((image) => {
              if (!image.complete) {
                image.addEventListener('load', () => this.slider.calcNavButtonsPosition(), { once: true });
              }
            });
          }
        }
      }

      renderEmpty() {
        this.removeAttribute('aria-busy');

        if (window.Shopify?.designMode) {
          this.hideStatus();
          if (this.grid) {
            this.grid.innerHTML = `<p class="rebuy-recommendations__empty text-center">${this.emptyMessage}</p>`;
          }
          if (this.slider) this.slider.hidden = false;
          this.classList.add('is-empty');
          return;
        }

        this.closest('.shopify-section')?.remove();
      }
    }
  );
}
